import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/misc/Button";
import { Label } from "../../components/misc/Label";
import { Input } from "../../components/misc/Input";
import { Switch } from "../../components/misc/Switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/misc/Select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../components/misc/Popover";
import { CalendarIcon, Clock } from "lucide-react";
import { format, addWeeks } from "date-fns";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Plan } from "../types";
import { getAuthHeader } from "../../utils/authHeader";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import LoadingScreen from "../../screens/LoadingScreen";
import CustomDatePicker from "../../components/misc/CutomDatePicker";

interface FreeTrialFormProps {
    onSuccess: () => void;
    onCancel: () => void;
}

export const FreeTrialForm: React.FC<FreeTrialFormProps> = ({
    onSuccess,
    onCancel,
}) => {
    const isMobile = useIsMobile(900);
    const [loading, setLoading] = useState(false);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [formData, setFormData] = useState({
        code: "",
        planId: "",
        maxUserCount: "100",
        trialDays: "14",
        isActive: true,
    });
    const [expiryDate, setExpiryDate] = useState<Date>(() => {
        const dt = addWeeks(new Date(), 4);
        dt.setHours(23, 59, 0, 0);
        return dt;
    });
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [expiryHour, setExpiryHour] = useState<string>(() => {
        const h = expiryDate.getHours() % 12 || 12;
        return h.toString();
    });
    const [expiryMinute, setExpiryMinute] = useState<string>(() => {
        return expiryDate.getMinutes().toString();
    });
    const [expiryPeriod, setExpiryPeriod] = useState<string>(() =>
        expiryDate.getHours() >= 12 ? "PM" : "AM"
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get<Plan[]>(
                `${backendUrl}/admin/get-plan`,
                { headers }
            );
            setPlans(response.data);
        } catch {
            toast.error(
                <CustomToast message="Failed to fetch plans" type="error" />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    // Handlers for date/time changes
    const handleExpiryDateChange = (date: Date) => {
        const newDt = new Date(date);
        newDt.setHours(expiryDate.getHours(), expiryDate.getMinutes(), 0, 0);
        setExpiryDate(newDt);
    };

    const handleHourChange = (value: string) => {
        const hr = parseInt(value, 10) % 12;
        const base = expiryPeriod === "PM" ? hr + 12 : hr;
        const dt = new Date(expiryDate);
        dt.setHours(base, dt.getMinutes());
        setExpiryDate(dt);
        setExpiryHour(value);
    };

    const handleMinuteChange = (value: string) => {
        const min = parseInt(value, 10);
        const dt = new Date(expiryDate);
        dt.setMinutes(min);
        setExpiryDate(dt);
        setExpiryMinute(value);
    };

    const handlePeriodChange = (value: string) => {
        const hr = parseInt(expiryHour, 10) % 12;
        const newHour = value === "PM" ? hr + 12 : hr;
        const dt = new Date(expiryDate);
        dt.setHours(newHour);
        setExpiryDate(dt);
        setExpiryPeriod(value);
    };

    const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Validate required
        if (
            !formData.code ||
            !formData.planId ||
            !formData.trialDays ||
            !formData.maxUserCount
        ) {
            toast.error(
                <CustomToast
                    message="Please fill in all required fields"
                    type="error"
                />
            );
            return;
        }
        setIsSubmitting(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const payload = {
                code: formData.code.toUpperCase(),
                applicablePlan: formData.planId,
                maxUserCount: parseInt(formData.maxUserCount, 10),
                expiresAt: expiryDate.toISOString(),
                trialDays: parseInt(formData.trialDays, 10),
                isActive: formData.isActive,
            };
            const res = await axios.post(
                `${backendUrl}/admin/free-trials`,
                payload,
                {
                    headers,
                }
            );

            if (res.data.success === false) {
                toast.error(
                    <CustomToast
                        message={
                            res.data.message || "Error creating free trial code"
                        }
                        type="error"
                    />
                );
                return;
            }

            toast.success(
                <CustomToast message="Free trial code created" type="success" />
            );
            onSuccess();
        } catch {
            toast.error(
                <CustomToast
                    message="Error creating free trial code"
                    type="error"
                />
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {loading && <LoadingScreen />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="code">Free Trial Code</Label>
                        <Input
                            id="code"
                            value={formData.code}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    code: e.target.value,
                                }))
                            }
                            placeholder="FREETRIAL2025"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="planId">Applicable Plan</Label>
                        <Select
                            value={formData.planId}
                            onValueChange={(value) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    planId: value,
                                }))
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select plan" />
                            </SelectTrigger>
                            <SelectContent>
                                {plans.map((plan) => (
                                    <SelectItem
                                        key={plan.id}
                                        value={plan.id || ""}
                                    >
                                        {plan.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="trialDays">Trial Duration (days)</Label>
                        <Input
                            id="trialDays"
                            type="number"
                            min="1"
                            value={formData.trialDays}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    trialDays: e.target.value,
                                }))
                            }
                            required
                        />
                    </div>
                </div>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="maxUserCount">
                            Maximum Usage Count
                        </Label>
                        <Input
                            id="maxUserCount"
                            type="number"
                            min="1"
                            value={formData.maxUserCount}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    maxUserCount: e.target.value,
                                }))
                            }
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="expiresAt">Expiration Date</Label>
                        <div
                            className={cn(
                                "flex items-center space-x-2 text-primary",
                                isMobile ? "flex-col space-y-2" : "flex-row"
                            )}
                        >
                            <Popover
                                open={isCalendarOpen}
                                onOpenChange={setIsCalendarOpen}
                            >
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "w-[240px] justify-start text-left font-normal",
                                            !expiryDate &&
                                                "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {format(expiryDate, "PPP")}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-auto p-0"
                                    align="start"
                                >
                                    <CustomDatePicker
                                        date={expiryDate}
                                        setDate={handleExpiryDateChange}
                                        restriction="none"
                                    />
                                </PopoverContent>
                            </Popover>
                            <div className="flex items-center space-x-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <Select
                                    value={expiryHour}
                                    onValueChange={handleHourChange}
                                >
                                    <SelectTrigger className="w-[70px]">
                                        <SelectValue placeholder="HH" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {hourOptions.map((h) => (
                                            <SelectItem
                                                key={h}
                                                value={h.toString()}
                                            >
                                                {h.toString().padStart(2, "0")}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select
                                    value={expiryMinute}
                                    onValueChange={handleMinuteChange}
                                >
                                    <SelectTrigger className="w-[70px]">
                                        <SelectValue placeholder="MM" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {minuteOptions.map((m) => (
                                            <SelectItem
                                                key={m}
                                                value={m.toString()}
                                            >
                                                {m.toString().padStart(2, "0")}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select
                                    value={expiryPeriod}
                                    onValueChange={handlePeriodChange}
                                >
                                    <SelectTrigger className="w-[70px]">
                                        <SelectValue placeholder="AM/PM" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="AM">AM</SelectItem>
                                        <SelectItem value="PM">PM</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Switch
                            id="isActive"
                            checked={formData.isActive}
                            onCheckedChange={(checked) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    isActive: checked,
                                }))
                            }
                        />
                        <Label htmlFor="isActive">Active</Label>
                    </div>
                </div>
            </div>
            <div className="flex gap-3">
                <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Creating..." : "Create Free Trial Code"}
                </Button>
                <Button type="button" variant="outline" onClick={onCancel}>
                    Cancel
                </Button>
            </div>
        </form>
    );
};

export default FreeTrialForm;
